import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, formatGHS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Paginator, { usePagination } from "@/components/Paginator";
import { Plus, Trash2, Pencil, Save, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isRevenueAccount } from "@/lib/accountFilters";
import { FormDisclosure, LabeledInput, LabeledTextarea } from "@/components/HospitalFormControls";
import SearchableSelect from "@/components/SearchableSelect";

/**
 * Inline service and fee catalog with CRUD controls.
 */
export default function ServicesCatalogModal({ onChanged, apiPath = "/services", open, onOpenChange }) {
  const { user } = useAuth();
  const canEdit = ["tenant_admin", "finance_manager", "accountant", "accounts_officer", "super_admin"].includes(user?.role);
  const [services, setServices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const editFormRef = useRef(null);
  const { page, setPage, pageSize, setPageSize, paged, total } = usePagination(services, 20);
  const dialogMode = open !== undefined;
  const isHospitalCatalog = apiPath.startsWith("/hospital/");

  function blank() {
    return { service_code: "", name: "", description: "", unit_price: 0, revenue_account_id: "", is_active: true };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.get(apiPath), api.get("/accounts")]);
      setServices(s.data);
      setAccounts(a.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!editingId) return;
    editFormRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    editFormRef.current?.querySelector("input, textarea")?.focus({ preventScroll: true });
  }, [editingId]);

  const startEdit = (svc) => {
    setEditingId(svc.service_id);
    setForm({
      service_code: svc.service_code || "",
      name: svc.name || "",
      description: svc.description || "",
      unit_price: Number(svc.unit_price || 0),
      revenue_account_id: svc.revenue_account_id || "",
      is_active: svc.is_active !== false,
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(blank()); };

  const save = async () => {
    if (!form.service_code.trim()) return toast.error("Service code is required");
    if (!form.name.trim()) return toast.error("Service name is required");
    if (Number(form.unit_price) < 0) return toast.error("Unit price must be ≥ 0");
    setSaving(true);
    try {
      const payload = {
        service_code: form.service_code.trim(),
        name: form.name.trim(),
        description: form.description || null,
        unit_price: Number(form.unit_price),
        revenue_account_id: form.revenue_account_id || null,
        is_active: !!form.is_active,
      };
      const servicePath = `${apiPath}/${editingId}`;
      if (editingId) {
        await api.put(servicePath, payload);
        toast.success("Service updated");
      } else {
        await api.post(apiPath, payload);
        toast.success("Service added");
      }
      cancelEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"?`)) return;
    try {
      await api.delete(`${apiPath}/${svc.service_id}`);
      toast.success("Service deleted");
      if (editingId === svc.service_id) cancelEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revenueAccounts = accounts.filter((a) => isRevenueAccount(a));
  const serviceForm = (
    <div className="space-y-3" data-testid="services-form">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {editingId ? "Edit service" : "Add new service"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <LabeledInput label="Code" value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} placeholder="e.g. CONSULT-01" data-testid="svc-code" />
        <div className="md:col-span-2">
          <LabeledInput label="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Specialist consultation" data-testid="svc-name" />
        </div>
        <LabeledInput label="Unit price (GHS)" type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="svc-price" />
        <div className="md:col-span-2">
          <LabeledTextarea label="Description (optional)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="svc-desc" />
        </div>
        <div className="md:col-span-2">
          <p className="mb-1 text-[11px] font-medium text-muted-foreground">Revenue account (optional)</p>
          <SearchableSelect
            options={[
              { value: "none", label: "Use default services revenue" },
              ...revenueAccounts.map((account) => ({ value: account.account_id, label: `${account.account_code} — ${account.account_name}` })),
            ]}
            value={form.revenue_account_id || "none"}
            onChange={(value) => setForm({ ...form, revenue_account_id: value === "none" ? "" : value })}
            placeholder="Select revenue account"
            searchPlaceholder="Search revenue accounts"
            className="h-10"
            testId="svc-account"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="svc-active" />
          <span className="font-mono uppercase tracking-widest">Active (show in POS grid)</span>
        </label>
        <div className="flex gap-2">
          {editingId && <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="svc-cancel"><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>}
          <Button size="sm" onClick={save} disabled={saving} data-testid="svc-save">
            {editingId ? <Save className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            {saving ? "Saving…" : editingId ? "Save changes" : "Add service"}
          </Button>
        </div>
      </div>
    </div>
  );

  const catalogContent = (
    <section className="space-y-4" data-testid="hospital-service-settings">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div><h2 className="font-semibold">{isHospitalCatalog ? "Services & Fees" : dialogMode ? "Service catalog" : "Manage services"}</h2><p className="text-sm text-muted-foreground">{isHospitalCatalog ? "Hospital service fees are available in the Hospital POS and approved tariff schedules." : "Create and maintain services available in Services Invoicing POS."}</p></div>
        <Button type="button" size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>
      </div>

      {canEdit && (editingId ? <div ref={editFormRef} className="border-b border-border pb-4">{serviceForm}</div> : <FormDisclosure title="Add service or fee">{serviceForm}</FormDisclosure>)}

        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="text-[10px] uppercase tracking-widest">Code</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Description</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Price</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Active</TableHead>
                {canEdit && <TableHead className="text-[10px] uppercase tracking-widest text-right w-24">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : services.length === 0 ? (
                <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-muted-foreground">
                  No services yet — add your first one above to enable click-to-add in the POS.
                </TableCell></TableRow>
              ) : paged.map((s) => (
                <TableRow key={s.service_id} data-testid={`svc-row-${s.service_code}`}>
                  <TableCell className="font-mono text-xs py-2">{s.service_code}</TableCell>
                  <TableCell className="py-2 text-sm font-medium">{s.name}</TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground max-w-xs truncate">{s.description || "—"}</TableCell>
                  <TableCell className="py-2 text-right font-mono">{formatGHS(s.unit_price)}</TableCell>
                  <TableCell className="py-2 text-xs font-mono uppercase">
                    {s.is_active === false ? (
                      <span className="text-muted-foreground">Off</span>
                    ) : (
                      <span className="text-emerald-600 dark:text-emerald-400">On</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="py-2 text-right space-x-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(s)} data-testid={`svc-edit-${s.service_code}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => remove(s)} data-testid={`svc-delete-${s.service_code}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Paginator
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sizes={[20]}
          testIdPrefix="hospital-services"
        />

    </section>
  );

  if (dialogMode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto" data-testid="services-catalog-dialog">
          <DialogHeader><DialogTitle>Manage Services</DialogTitle></DialogHeader>
          {catalogContent}
        </DialogContent>
      </Dialog>
    );
  }

  return catalogContent;
}
